import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Search } from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { PageError, PageLoader } from "@/components/ui/PageStates";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { EnquiryModal } from "@/components/overlays/EnquiryModal";
import { useContent } from "@/lib/content-context";

export default function FaqPage() {
  const { data, loading, error } = useContent();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [askOpen, setAskOpen] = useState(false);

  useEffect(() => {
    document.title = "FAQ - Goldmap EA";
    window.scrollTo({ top: 0 });
  }, []);

  const filtered = useMemo(() => {
    const list = data?.faqs || [];
    const q = query.trim().toLowerCase();
    return list.filter((f) => {
      const matchCat = cat === "all" || f.category === cat;
      const matchQ =
        !q ||
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [data, query, cat]);

  if (loading) {
    return (
      <SiteLayout config={null}>
        <PageLoader />
      </SiteLayout>
    );
  }
  if (error || !data) {
    return (
      <SiteLayout config={null}>
        <PageError onRetry={() => window.location.reload()} />
      </SiteLayout>
    );
  }

  const cats = [{ slug: "all", label: "All" }, ...(data.faq_categories || [])];

  return (
    <SiteLayout config={data.config} availability={data.availability}>
      <section className="gm-hero-bg relative pb-10 pt-12">
        <div className="gm-container">
          <Link
            to="/"
            data-testid="faq-back-link"
            className="inline-flex items-center gap-2 text-xs font-semibold"
            style={{ color: "var(--gm-muted)" }}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>
          <h1 className="mt-5 text-3xl font-extrabold text-white sm:text-4xl">
            Frequently asked questions
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed" style={{ color: "var(--gm-muted)" }}>
            Everything about setup, brokers, risk and licensing. Search dulu - kalau tak
            jumpa, just tanya us.
          </p>

          <div className="relative mt-6 max-w-lg">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
              style={{ color: "var(--gm-muted-2)" }}
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions..."
              data-testid="faq-search-input"
              className="h-12 pl-10"
            />
          </div>
        </div>
      </section>

      <section className="gm-section pt-8">
        <div className="gm-container">
          <div className="flex flex-wrap gap-2" data-testid="faq-page-filters">
            {cats.map((c) => {
              const activeCat = cat === c.slug;
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCat(c.slug)}
                  data-testid={`faq-filter-${c.slug}`}
                  className="min-h-[40px] rounded-full px-4 text-xs font-semibold"
                  style={{
                    background: activeCat ? "var(--gm-red)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${
                      activeCat ? "var(--gm-red)" : "rgba(255,255,255,0.12)"
                    }`,
                    color: activeCat ? "#fff" : "var(--gm-muted)",
                    transition: "background-color 180ms ease, border-color 180ms ease",
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <p className="mt-5 text-xs" style={{ color: "var(--gm-muted-2)" }}>
            Showing {filtered.length} of {data.faqs?.length || 0} questions
          </p>

          {filtered.length ? (
            <Accordion
              type="multiple"
              className="mt-4 space-y-3"
              data-testid="faq-page-accordion"
            >
              {filtered.map((f) => (
                <AccordionItem
                  key={f.slug}
                  value={f.slug}
                  data-testid={`faq-page-item-${f.slug}`}
                  className="overflow-hidden rounded-xl border px-4"
                  style={{
                    background: "var(--gm-navy-925)",
                    borderColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <AccordionTrigger className="py-4 text-left text-sm font-semibold text-white hover:no-underline">
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent
                    className="pb-4 text-sm leading-relaxed"
                    style={{ color: "var(--gm-muted)" }}
                  >
                    {f.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div
              className="mt-6 rounded-xl p-8 text-center"
              data-testid="faq-empty-state"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px dashed rgba(255,255,255,0.16)",
              }}
            >
              <p className="text-sm font-semibold text-white">
                Tak jumpa anything for that search
              </p>
              <p className="mt-1.5 text-xs" style={{ color: "var(--gm-muted)" }}>
                Try a shorter keyword, or ask us directly.
              </p>
              <button
                type="button"
                onClick={() => setAskOpen(true)}
                data-testid="faq-empty-ask-button"
                className="gm-btn-red mt-5 inline-flex min-h-[44px] items-center gap-2 rounded-xl px-4 text-sm font-bold"
              >
                <MessageCircle className="h-4 w-4" />
                Ask Goldmap
              </button>
            </div>
          )}
        </div>
      </section>

      <EnquiryModal open={askOpen} onOpenChange={setAskOpen} config={data.config} />
    </SiteLayout>
  );
}
